import { forwardRef, useImperativeHandle } from 'react';
import * as React from 'react';
import './style.scss';
import ValidationInfo from '../../../validation/validation-info';
import RequiredAsterisk from '../RequiredAsterisk';
import type IValidatable from '../validatable.interface';
import type { IServerValidationError } from '../validatable.interface';
import ValidationEngine from '../../../validation/validation-engine';

interface ISelectInputGroupState {
  value: string;
  validationInfo: ValidationInfo;
}

interface ISelectInputGroupProps {
  id?: string;
  serverValidationName?: string;
  className?: string;
  labelText?: string;
  hideLabel?: boolean;
  placeholder?: string;
  required?: boolean;
  hideRequiredAsterisk?: boolean;
  disabled?: boolean;
  options: { value: string; text: string; ariaLabel?: string }[];
  state: ISelectInputGroupState;
  onChange?: (value: string) => void;
  onSelect?: (value: string) => void;
}

export default forwardRef<IValidatable, ISelectInputGroupProps>(function SelectInputGroup(
  {
    id,
    serverValidationName = undefined,
    className,
    labelText,
    hideLabel = false,
    placeholder,
    required = false,
    hideRequiredAsterisk = false,
    disabled = false,
    options,
    state,
    onChange,
    onSelect,
  }: ISelectInputGroupProps,
  ref
) {
  // === INTERFACE IMPLEMENTATION ===
  useImperativeHandle(ref, () => ({
    checkValidity(): ValidationInfo {
      const updatedValidationInfo = new ValidationInfo(`${labelText ?? placeholder} - `);
      ValidationEngine.for(updatedValidationInfo).requiredIf(required, state.value).validate();
      return updatedValidationInfo;
    },
    handleServerValidationErrors(errors: IServerValidationError[]): ValidationInfo {
      return new ValidationInfo(
        `${labelText ?? placeholder} - `,
        errors
          .filter((e) => e.name === serverValidationName)
          .map((e) => e.errorMessages)
          .reduce((a, b) => a.concat(b), [])
      );
    },
  }));

  // === EVENT HANDLERS ===
  const handleSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    onChange?.(event.target.value);
  };

  return (
    <div className={`select-input-group input-group ${className ?? ''}`}>
      {labelText && (
        <label htmlFor={id} className={`form-label ${hideLabel ? 'visually-hidden' : ''}`.trim()}>
          {labelText}
          {required && !hideRequiredAsterisk && <RequiredAsterisk />}
        </label>
      )}
      <div className="input-group">
        <select
          id={id}
          className={`form-select ${state.validationInfo.isInvalid ? 'is-invalid' : ''}`}
          value={state.value || ''}
          disabled={disabled}
          onChange={handleSelectChange}
          aria-invalid={state.validationInfo.isInvalid}
          aria-describedby={state.validationInfo.isInvalid ? 'invalid-feedback' : undefined}
        >
          {placeholder && (
            <option value={''} disabled>
              {placeholder}
            </option>
          )}
          {options.map(({ value, text, ariaLabel }) => (
            <option key={value} value={value} aria-label={ariaLabel}>
              {text}
            </option>
          ))}
        </select>
        <button className="btn btn-primary py-0" type="button" onClick={() => onSelect?.(state.value)}>
          Välj
        </button>
        {state.validationInfo.isInvalid && (
          <div className="invalid-feedback">
            {state.validationInfo.errors.map((error) => (
              <div key={error}>{error}</div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
});
